import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { format } from "date-fns";
import Plot from "react-plotly.js";
import { toast } from "react-toastify";

import {
  supportCompareSalesDatesRuns,
  supportGetSalesDatesRun,
  supportGetSalesDatesRunUser,
  supportGetSalesDatesRuns,
  supportTriggerSalesDatesRun,
} from "src/apiService/modules/support";
import type {
  SalesDatesCompareResult,
  SalesDatesRunDetail,
  SalesDatesRunSummary,
  SalesDatesUserRow,
  SalesDatesUserSale,
  SalesDatesUsersByVersion,
  SalesDatesVersionCategory,
} from "src/interfaces/salesDatesRuns.interface";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken, supportIsAdmin } from "src/store/support/selector";

const PLOT_COLORS = {
  onNewestBuild: "#2ca02c",
  outdatedClient: "#ff7f0e",
  noVersion: "#d62728",
  unknown: "#7f7f7f",
};

const VERSION_TABS: {
  key: SalesDatesVersionCategory;
  label: string;
  color: string;
}[] = [
  { key: "on_newest_build", label: "Current build", color: PLOT_COLORS.onNewestBuild },
  { key: "outdated_client", label: "Old build", color: PLOT_COLORS.outdatedClient },
  { key: "no_version", label: "No version", color: PLOT_COLORS.noVersion },
  { key: "unknown", label: "Unknown", color: PLOT_COLORS.unknown },
];

const BUCKET_LABELS: Record<string, string> = {
  server: "Integration (server)",
  client: "Integration (client)",
  manual: "Manual",
  other: "Other",
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  chart: {
    width: "100%",
    minHeight: 360,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  compareRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginTop: theme.spacing(2),
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  statBox: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  versionTabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  expandedCell: {
    paddingBottom: theme.spacing(2),
    paddingTop: 0,
    backgroundColor: theme.palette.action.hover,
  },
  sourceTable: {
    marginTop: theme.spacing(1),
    maxWidth: 640,
  },
}));

function handleSupportError(err: any, dispatch: ReturnType<typeof useDispatch>) {
  if (err?.code === "failed-precondition") {
    dispatch(setSupportLoginToken());
  }
  toast.error(err?.message || "Request failed");
}

function formatRunDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return format(new Date(iso), "MMM d, yyyy HH:mm");
  } catch {
    return iso;
  }
}

function buildRunsChart(runs: SalesDatesRunSummary[]) {
  const completed = runs
    .filter((r) => r.status === "complete")
    .slice()
    .reverse();

  const x = completed.map((r) => formatRunDate(r.startedAt));

  return [
    {
      x,
      y: completed.map((r) => r.versionBreakdown?.onNewestBuild ?? 0),
      name: "Current build",
      type: "bar" as const,
      marker: { color: PLOT_COLORS.onNewestBuild },
    },
    {
      x,
      y: completed.map((r) => r.versionBreakdown?.outdatedClient ?? 0),
      name: "Old build",
      type: "bar" as const,
      marker: { color: PLOT_COLORS.outdatedClient },
    },
    {
      x,
      y: completed.map((r) => r.versionBreakdown?.noVersion ?? 0),
      name: "No version",
      type: "bar" as const,
      marker: { color: PLOT_COLORS.noVersion },
    },
  ];
}

function StatBlock({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.statBox}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

function formatReviewMatch(sale: SalesDatesUserSale) {
  if (
    sale.unreviewed === undefined &&
    sale.unmatched === undefined &&
    sale.matchedToInventory === undefined
  ) {
    return "—";
  }
  const review = sale.unreviewed ? "unreviewed" : "reviewed";
  const match = sale.unmatched
    ? "unmatched"
    : sale.matchedToInventory
      ? "matched"
      : "not matched";
  return `${review} · ${match}`;
}

function formatSaleDate(iso: string | null) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString();
  } catch {
    return iso;
  }
}

function inferVersionCategory(row: SalesDatesUserRow): SalesDatesVersionCategory {
  if (row.versionCategory) return row.versionCategory;
  if (row.noVersion) return "no_version";
  if (row.outdatedClient) return "outdated_client";
  if (row.onNewestBuild) return "on_newest_build";
  return "unknown";
}

function groupUsersByVersion(
  users: SalesDatesUserRow[],
  usersByVersion?: SalesDatesUsersByVersion | null
): SalesDatesUsersByVersion {
  if (usersByVersion) return usersByVersion;
  const grouped: SalesDatesUsersByVersion = {
    on_newest_build: [],
    outdated_client: [],
    no_version: [],
    unknown: [],
  };
  for (const row of users) {
    grouped[inferVersionCategory(row)].push(row);
  }
  return grouped;
}

function ChangeTypeChips({ row }: { row: SalesDatesUserRow }) {
  const classes = useStyles();
  const cs = row.changeStats;
  if (!cs) return null;
  return (
    <Box className={classes.chipRow}>
      <Chip size="small" label={`null min ${cs.nullMin}`} />
      <Chip size="small" label={`min real ${cs.minReal}`} />
      <Chip size="small" label={`max real ${cs.maxReal}`} />
      <Chip size="small" label={`null max ${cs.nullMax}`} />
    </Box>
  );
}

function SourceBreakdown({ row }: { row: SalesDatesUserRow }) {
  const classes = useStyles();
  const entries = Object.entries(row.bySource || {}).sort(
    (a, b) => b[1] - a[1]
  );
  if (!entries.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        No source breakdown (re-run backfill for this data).
      </Typography>
    );
  }
  return (
    <Table size="small" className={classes.sourceTable}>
      <TableHead>
        <TableRow>
          <TableCell>Sale source</TableCell>
          <TableCell align="right">Count</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([key, count]) => (
          <TableRow key={key}>
            <TableCell>{key.replace(/\s*\|\s*/g, " · ")}</TableCell>
            <TableCell align="right">{count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function UserSalesDialog({
  open,
  onClose,
  runId,
  user,
  token,
  dispatch,
}: {
  open: boolean;
  onClose: () => void;
  runId: string;
  user: SalesDatesUserRow | null;
  token: string;
  dispatch: ReturnType<typeof useDispatch>;
}) {
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState<SalesDatesUserSale[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const pageSize = 50;

  const loadSales = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (!token || !user) return;
      setLoading(true);
      setUnavailable(false);
      try {
        const { data } = await supportGetSalesDatesRunUser(
          token,
          runId,
          user.uid,
          { limit: pageSize, offset: nextOffset }
        );
        setSales((prev) => (append ? [...prev, ...data.sales] : data.sales));
        setTotal(data.total);
        setOffset(nextOffset + data.sales.length);
      } catch (err: any) {
        if (err?.code === "not-found") {
          setUnavailable(true);
        } else {
          handleSupportError(err, dispatch);
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, runId, token, user]
  );

  useEffect(() => {
    if (!open || !user) return;
    setSales([]);
    setTotal(0);
    setOffset(0);
    void loadSales(0, false);
  }, [open, user, loadSales]);

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Sales for {user.email}
        <Typography variant="body2" color="textSecondary">
          {user.count} updated · {user.clientVersion || "(no version)"}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {unavailable ? (
          <Typography variant="body2" color="textSecondary">
            Per-sale detail is not stored for this run. Run a new backfill to
            capture sale-level breakdown.
          </Typography>
        ) : loading && sales.length === 0 ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sale ID</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Bucket</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Review / match</TableCell>
                  <TableCell>Min date</TableCell>
                  <TableCell>Max date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.map((sale) => (
                  <TableRow key={sale.saleId}>
                    <TableCell>{sale.saleId}</TableCell>
                    <TableCell>
                      {sale.change.nullMin && "null min "}
                      {sale.change.minReal && "min real "}
                      {sale.change.maxReal && "max real "}
                      {sale.change.nullMax && "null max"}
                    </TableCell>
                    <TableCell>{BUCKET_LABELS[sale.bucket] || sale.bucket}</TableCell>
                    <TableCell>{sale.sourceLabel}</TableCell>
                    <TableCell>{sale.salePlatform || "—"}</TableCell>
                    <TableCell>{formatReviewMatch(sale)}</TableCell>
                    <TableCell>
                      {formatSaleDate(sale.prevMin)} → {formatSaleDate(sale.nextMin)}
                    </TableCell>
                    <TableCell>
                      {formatSaleDate(sale.prevMax)} → {formatSaleDate(sale.nextMax)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {offset < total && (
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={() => void loadSales(offset, true)}
                >
                  {loading ? "Loading…" : `Load more (${sales.length} / ${total})`}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function UserRow({
  row,
  onViewSales,
}: {
  row: SalesDatesUserRow;
  onViewSales: (row: SalesDatesUserRow) => void;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const buckets = row.importBuckets;

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {row.email !== "(auth-user-not-found)" ? (
            <Link to={`/support/user/${row.email}`}>{row.email}</Link>
          ) : (
            row.email
          )}
        </TableCell>
        <TableCell>{row.uid}</TableCell>
        <TableCell align="right">{row.count}</TableCell>
        <TableCell>
          {row.noVersion ? "(no version)" : row.clientVersion || "—"}
        </TableCell>
        <TableCell>
          <Button size="small" color="primary" onClick={() => onViewSales(row)}>
            View sales
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} className={classes.expandedCell} style={{ padding: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box p={2}>
              <Typography variant="subtitle2" gutterBottom>
                Change types
              </Typography>
              <ChangeTypeChips row={row} />
              {buckets && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Sale type
                  </Typography>
                  <Typography variant="body2">
                    {BUCKET_LABELS.server} {buckets.server} ·{" "}
                    {BUCKET_LABELS.client} {buckets.client} ·{" "}
                    {BUCKET_LABELS.manual} {buckets.manual} ·{" "}
                    {BUCKET_LABELS.other} {buckets.other}
                  </Typography>
                </Box>
              )}
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  By integration / source
                </Typography>
                <SourceBreakdown row={row} />
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function UsersByVersionPanel({
  runId,
  users,
  usersByVersion,
  token,
  dispatch,
}: {
  runId: string;
  users: SalesDatesUserRow[];
  usersByVersion?: SalesDatesUsersByVersion | null;
  token: string;
  dispatch: ReturnType<typeof useDispatch>;
}) {
  const classes = useStyles();
  const grouped = useMemo(
    () => groupUsersByVersion(users, usersByVersion),
    [users, usersByVersion]
  );
  const [tab, setTab] = useState(0);
  const [salesUser, setSalesUser] = useState<SalesDatesUserRow | null>(null);

  const activeKey = VERSION_TABS[tab]?.key ?? "on_newest_build";
  const activeUsers = grouped[activeKey] || [];

  return (
    <Box mt={3}>
      <Typography variant="subtitle2" gutterBottom>
        Updated sales by user ({users.length})
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={classes.versionTabs}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        {VERSION_TABS.map(({ key, label, color }) => (
          <Tab
            key={key}
            label={`${label} (${grouped[key]?.length ?? 0})`}
            style={{ color }}
          />
        ))}
      </Tabs>
      {activeUsers.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          No users in this group for this run.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Email</TableCell>
              <TableCell>UID</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell>Client version</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {activeUsers.map((row) => (
              <UserRow
                key={row.uid}
                row={row}
                onViewSales={setSalesUser}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <UserSalesDialog
        open={!!salesUser}
        onClose={() => setSalesUser(null)}
        runId={runId}
        user={salesUser}
        token={token}
        dispatch={dispatch}
      />
    </Box>
  );
}

function RunDetail({
  detail,
  loading,
  token,
  dispatch,
}: {
  detail: SalesDatesRunDetail | null;
  loading: boolean;
  token: string;
  dispatch: ReturnType<typeof useDispatch>;
}) {
  const classes = useStyles();

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!detail) return null;

  const { run, users } = detail;
  const cs = run.changeStats;

  return (
    <Box className={classes.section}>
      <Typography variant="subtitle1" gutterBottom>
        Run {run.runId}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        {formatRunDate(run.startedAt)} — {run.status}
        {run.serverVersion ? ` · server ${run.serverVersion}` : ""}
      </Typography>

      <Box className={classes.detailGrid}>
        <StatBlock label="Scanned" value={run.scanned} />
        <StatBlock label="Updated" value={run.updated} />
        <StatBlock
          label="Current build updates"
          value={run.versionBreakdown?.onNewestBuild ?? 0}
        />
        <StatBlock
          label="Old build updates"
          value={run.versionBreakdown?.outdatedClient ?? 0}
        />
        <StatBlock
          label="No version updates"
          value={run.versionBreakdown?.noVersion ?? 0}
        />
      </Box>

      {cs && (
        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            Change types: null min {cs.nullMin}, min real {cs.minReal}, max
            real {cs.maxReal}
          </Typography>
          {run.importBuckets && (
            <Typography variant="body2" color="textSecondary">
              Import buckets: server {run.importBuckets.server}, client{" "}
              {run.importBuckets.client}, manual {run.importBuckets.manual},
              other {run.importBuckets.other}
            </Typography>
          )}
        </Box>
      )}

      {users && users.length > 0 && (
        <UsersByVersionPanel
          runId={run.runId}
          users={users}
          usersByVersion={detail.usersByVersion}
          token={token}
          dispatch={dispatch}
        />
      )}
    </Box>
  );
}

function ComparePanel({
  comparison,
}: {
  comparison: SalesDatesCompareResult | null;
}) {
  if (!comparison) return null;
  const { comparison: c } = comparison;

  return (
    <Box mt={2}>
      <Typography variant="subtitle2" gutterBottom>
        Comparison
      </Typography>
      <Typography variant="body2">
        Updated: {c.previousUpdated} → {c.currentUpdated}
      </Typography>
      <Typography variant="body2">
        Null min: {c.previousNullMin} → {c.currentNullMin}
      </Typography>
      <Typography variant="body2">
        Overlap (all / null min / min real): {c.overlapAllIds} /{" "}
        {c.overlapNullMinIds} / {c.overlapMinRealIds}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        New null-min this run: {c.onlyInCurrentNull} · new minReal:{" "}
        {c.onlyInCurrentMinReal} · minReal also in previous:{" "}
        {c.minRealSeenInPrevRun}
      </Typography>
    </Box>
  );
}

function SalesDates() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const isAdmin = useSelector(supportIsAdmin);

  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<SalesDatesRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [detail, setDetail] = useState<SalesDatesRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comparePreviousId, setComparePreviousId] = useState("");
  const [compareCurrentId, setCompareCurrentId] = useState("");
  const [comparison, setComparison] = useState<SalesDatesCompareResult | null>(
    null
  );
  const [starting, setStarting] = useState(false);

  const loadRuns = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await supportGetSalesDatesRuns(token, { limit: 100 });
      setRuns(data.runs);
      if (!selectedRunId && data.runs.length) {
        setSelectedRunId(data.runs[0].runId);
      }
    } catch (err: any) {
      handleSupportError(err, dispatch);
    } finally {
      setLoading(false);
    }
  }, [dispatch, token, selectedRunId]);

  const loadDetail = useCallback(
    async (runId: string) => {
      if (!token || !runId) return;
      setDetailLoading(true);
      try {
        const { data } = await supportGetSalesDatesRun(token, runId, {
          includeUsers: true,
        });
        setDetail(data);
      } catch (err: any) {
        handleSupportError(err, dispatch);
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [dispatch, token]
  );

  useEffect(() => {
    if (!isAdmin || !token) return;
    void loadRuns();
  }, [isAdmin, token, loadRuns]);

  useEffect(() => {
    if (!selectedRunId) return;
    void loadDetail(selectedRunId);
  }, [selectedRunId, loadDetail]);

  const chartData = useMemo(() => buildRunsChart(runs), [runs]);

  const startRun = async () => {
    if (!token) return;
    setStarting(true);
    try {
      const { data } = await supportTriggerSalesDatesRun(token);
      toast.success(`Started run ${data.runId}`);
      await loadRuns();
      setSelectedRunId(data.runId);
    } catch (err: any) {
      handleSupportError(err, dispatch);
    } finally {
      setStarting(false);
    }
  };

  const runCompare = async () => {
    if (!token || !comparePreviousId || !compareCurrentId) return;
    try {
      const { data } = await supportCompareSalesDatesRuns(
        token,
        comparePreviousId,
        compareCurrentId
      );
      setComparison(data);
    } catch (err: any) {
      handleSupportError(err, dispatch);
    }
  };

  if (!isAdmin) {
    return (
      <Box m={2} component={Paper} className={classes.paper}>
        <Typography color="textSecondary">
          Admin access is required to view sales dates runs.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component={Paper} className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        Sales Dates Backfill
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Weekly min/max date backfill runs with version breakdown per updated
        sale.
      </Typography>

      <Box className={classes.controls}>
        <Button variant="outlined" color="primary" onClick={() => loadRuns()}>
          Refresh
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={starting}
          onClick={() => void startRun()}
        >
          {starting ? "Starting…" : "Run backfill"}
        </Button>
      </Box>

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {chartData[0].x.length > 0 && (
            <Plot
              className={classes.chart}
              useResizeHandler
              data={chartData}
              config={{ displaylogo: false, responsive: true }}
              layout={{
                barmode: "stack",
                height: 360,
                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",
                font: { color: theme.palette.text.primary },
                margin: { l: 48, r: 16, t: 24, b: 80 },
                xaxis: { title: "Run", tickangle: -30 },
                yaxis: { title: "Updated sales" },
                legend: { orientation: "h", y: 1.12 },
              }}
            />
          )}

          <Table size="small" className={classes.section}>
            <TableHead>
              <TableRow>
                <TableCell>Run ID</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Updated</TableCell>
                <TableCell align="right">Current</TableCell>
                <TableCell align="right">Old build</TableCell>
                <TableCell align="right">No version</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow
                  key={run.runId}
                  hover
                  selected={run.runId === selectedRunId}
                  onClick={() => setSelectedRunId(run.runId)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>{run.runId}</TableCell>
                  <TableCell>{formatRunDate(run.startedAt)}</TableCell>
                  <TableCell>{run.status}</TableCell>
                  <TableCell align="right">{run.updated}</TableCell>
                  <TableCell align="right">
                    {run.versionBreakdown?.onNewestBuild ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {run.versionBreakdown?.outdatedClient ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {run.versionBreakdown?.noVersion ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <RunDetail
            detail={detail}
            loading={detailLoading}
            token={token}
            dispatch={dispatch}
          />

          <Box className={classes.section}>
            <Typography variant="subtitle1" gutterBottom>
              Compare runs
            </Typography>
            <Box className={classes.compareRow}>
              <TextField
                select
                label="Previous run"
                value={comparePreviousId}
                onChange={(e) => setComparePreviousId(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 220 }}
              >
                {runs.map((r) => (
                  <MenuItem key={r.runId} value={r.runId}>
                    {r.runId} ({formatRunDate(r.startedAt)})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Current run"
                value={compareCurrentId}
                onChange={(e) => setCompareCurrentId(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 220 }}
              >
                {runs.map((r) => (
                  <MenuItem key={r.runId} value={r.runId}>
                    {r.runId} ({formatRunDate(r.startedAt)})
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                color="primary"
                disabled={!comparePreviousId || !compareCurrentId}
                onClick={() => void runCompare()}
              >
                Compare
              </Button>
            </Box>
            <ComparePanel comparison={comparison} />
          </Box>
        </>
      )}
    </Box>
  );
}

export default SalesDates;
